import React, { useCallback, useState, useEffect } from 'react';
import { navigate } from 'gatsby';
import cx from 'classnames';

import { paths } from '@src/constants/pathsEnum';
import { nav, allNavURLS } from './payload';

import { Button } from '@commons/button/Button';
import { Link } from '@commons/link/Link';
import { ListRender } from '@commons/list-render/ListRender';
import { WebinarsContainer } from './webinars-container/WebinarsContainer';

import logo from '@images/codilime-logo/CodiLime_logo.svg';
import logoMobile from '@images/codilime-logo/CodiLime_logo_mobile.svg';

import * as styles from './navbar.module.scss';

function SubMenu({ menu, webinars, depth = 0, className, onLinkClick, categoryLabel = '' }) {
  return (
    <ListRender.Ul className={cx(className, styles[`depth${depth}`])}>
      {depth === 1 && <WebinarsContainer classNames={{ container: styles.webinarContainer }} webinars={webinars} />}
      <div className={styles[`containerWithPositionsDepth${depth}`]}>
        {depth === 1 && <h4 className={styles.categoryTitle}>{categoryLabel}</h4>}
        {menu.map(({ label, link, menu, depth: d = depth - 1 }, i) => {
          if (depth === 0)
            return (
              <ListRender.Li key={i}>
                <Link href={link} onClick={onLinkClick} onLinkClick={onLinkClick}>
                  {label}
                </Link>
              </ListRender.Li>
            );
          if (depth === 1)
            return (
              <div key={i}>
                <ListRender.Ul>
                  <ListRender.Li key={i}>
                    <h4>{label}</h4>
                  </ListRender.Li>
                  <SubMenu menu={menu} depth={d} onLinkClick={onLinkClick} />
                </ListRender.Ul>
              </div>
            );
          return null;
        })}
      </div>
    </ListRender.Ul>
  );
}

function Navigation({
  nav,
  path,
  children,
  onLinkClick = Function.prototype,
  setSubmenuOpen,
  isSubMenuOpen,
  hideSubmenu,
  setHideSubmenu,
}) {
  const [active, setActive] = useState(-1);
  const linkClickCallback = useCallback(
    (...args) => {
      setActive(-1);
      onLinkClick(...args);
    },
    [onLinkClick],
  );

  useEffect(() => {
    if (active !== -1) {
      setSubmenuOpen(true);
    } else {
      setSubmenuOpen(false);
    }
  }, [active]);

  useEffect(() => {
    if (hideSubmenu) {
      setActive(-1);
    }
  });
  return (
    <ListRender.Ul className={styles.menu}>
      {nav.map(({ label, menu = [], webinars = [], link, depth = 1 }, index) => {
        const seoClassName =
          label === 'Services' ? 'menu-services' : label === 'Knowledge' ? 'menu-knowledge' : undefined;
        return (
          <li
            key={index}
            className={cx(styles[`label${label}`], {
              [styles.hasMenu]: !!menu.length,
              [styles.active]: path.startsWith(link) || allNavURLS[index].some((link) => path.startsWith(link)),
              [styles.menuOpen]: active === index && isSubMenuOpen,
            })}
            onClick={() => {
              if (!isSubMenuOpen) {
                setActive(index === active ? -1 : index);
                setHideSubmenu(false);
                if (link) {
                  linkClickCallback();
                  navigate(link);
                }
              }
            }}
          >
            {link ? (
              <Link href={link} onClick={(e) => e.preventDefault()}>
                <p className="navbar-menu-item">{label}</p>
              </Link>
            ) : (
              <p className={cx(seoClassName, 'navbar-menu-item')}>{label}</p>
            )}
            {!!menu.length && (
              <SubMenu
                className={cx(styles.subMenu, { [styles.subMenuOpen]: isSubMenuOpen })}
                menu={menu}
                webinars={webinars}
                depth={depth - 1}
                onLinkClick={linkClickCallback}
                categoryLabel={label}
              />
            )}
          </li>
        );
      })}
      {children}
    </ListRender.Ul>
  );
}

export function Navbar(props) {
  const path = props.location.pathname;
  const { classNames: { mainNavigationContainer, buttonInMenu, menuBurger } = {}, logo: logoToOverride } = props;
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isSubMenuOpen, setSubmenuOpen] = useState(false);
  const [hideSubmenu, setHideSubmenu] = useState(true);

  const collapseSubmenu = () => {
    setSubmenuOpen(false);
    setHideSubmenu(true);
  };

  const Logo = ({
    showLime = false,
    alt = 'Codilime - Network and software engineering services company',
    classNames: { buttonInMenu } = {},
    logoToOverride,
  }) => {
    return (
      <div className={cx(styles.logo, buttonInMenu)}>
        <Link href={path !== '/' ? paths.HOME : ''}>
          <img
            src={logoToOverride || logo}
            loading="lazy"
            alt={alt}
            height="35"
            width="142"
            className={cx(styles.mainLogo, { [styles.hideMainLogo]: showLime })}
          />
          {showLime && (
            <img src={logoMobile} loading="lazy" alt={alt} height="35" width="35" className={styles.logoMobile} />
          )}
        </Link>
      </div>
    );
  };

  return (
    <nav className={cx(styles.mainNavigationContainer, mainNavigationContainer, isMenuOpen && 'navbar-active')}>
      <div className={styles.desktopContent}>
        <Logo logoToOverride={logoToOverride} classNames={{ buttonInMenu: cx(styles.buttonInMenu, buttonInMenu) }} />
        <Navigation
          nav={nav}
          path={path}
          setSubmenuOpen={setSubmenuOpen}
          isSubMenuOpen={false}
          hideSubmenu={hideSubmenu}
          setHideSubmenu={setHideSubmenu}
        />
        <Button href={paths.CONTACT} className={styles.contactButton} label="Contact Us" />
      </div>
      <div className={styles.mobileContent}>
        {isMenuOpen ? (
          isSubMenuOpen ? (
            <button className={cx(styles.backButton, styles.buttonInMenu)} onClick={collapseSubmenu}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  d="M10.5303 19.5303C10.2374 19.8232 9.76257 19.8232 9.46967 19.5303L2.46967 12.5304C2.32902 12.3897 2.25 12.199 2.25 12.0001C2.25 11.8012 2.32902 11.6104 2.46967 11.4697L9.46967 4.46974C9.76256 4.17685 10.2374 4.17685 10.5303 4.46974C10.8232 4.76263 10.8232 5.23751 10.5303 5.5304L4.81066 11.2501L21 11.2501C21.4142 11.2501 21.75 11.5859 21.75 12.0001C21.75 12.4143 21.4142 12.7501 21 12.7501L4.81067 12.7501L10.5303 18.4697C10.8232 18.7626 10.8232 19.2374 10.5303 19.5303Z"
                  fill="#A9ACAC"
                />
              </svg>
              <span>Main Menu</span>
            </button>
          ) : (
            <Logo showLime={isMenuOpen} classNames={{ buttonInMenu: cx(styles.buttonInMenu, buttonInMenu) }} />
          )
        ) : (
          <Logo
            logoToOverride={logoToOverride}
            showLime={isMenuOpen}
            classNames={{ buttonInMenu: cx(styles.buttonInMenu, buttonInMenu) }}
          />
        )}
        <div className={cx(styles.menuBurger, { [styles.crossIcon]: isMenuOpen }, menuBurger)}>
          <input
            id="menu_burger"
            type="checkbox"
            checked={isMenuOpen}
            onChange={(e) => setMenuOpen(e.target.checked)}
            readOnly
          />
          <label htmlFor="menu_burger">
            <span />
            <span />
            <span />
          </label>
          <aside className={styles.mobileNav}>
            <Navigation
              nav={nav}
              path={path}
              onLinkClick={() => setMenuOpen(false)}
              setSubmenuOpen={setSubmenuOpen}
              isSubMenuOpen={isSubMenuOpen}
              hideSubmenu={hideSubmenu}
              setHideSubmenu={setHideSubmenu}
            >
            </Navigation>
            <WebinarsContainer
              classNames={{
                container: cx(styles.webinarsOnMobile, {
                  [styles.hideWebinars]: isSubMenuOpen,
                }),
              }}
              webinars={nav[0]['webinars']}
            />
          </aside>
        </div>
      </div>
    </nav>
  );
}
